import React, { useState } from "react";
import { Modal, Spin, DatePicker } from "antd";
import "./style.css";
import PropTypes from "prop-types";
import Select from "react-select";
import { createEmployeeResign } from '../../../service/employeeResignService';
import Swal from "sweetalert2";
import { X } from "lucide-react";

const EmployeeResignModal = ({ isOpen, onClose, empRecord, recordId, onResignSuccess }) => {
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    const statusOptions = [
        { value: "Clearance Pending", label: "Clearance Pending" },
        { value: "Payment Pending", label: "Payment Pending" },
        { value: "Resigned", label: "Resigned" }
    ];
    
    const paymentOptions = [
        { value: "Cash Payment", label: "Cash Payment" },
        { value: "Bank Payment", label: "Bank Payment" },
    ];

    const handleReset = () => {
        setSelectedStatus(null);
        setSelectedPaymentOption(null);
        setReason("");
        setSelectedDate(null);
    };

    const showWarning = (str) => {
        return Swal.fire({
            title: "Error",
            text: str,
            icon: "error",
        });
    };

    const handleSubmit = async () => {
        if (!selectedStatus) {
            showWarning("Please select a status");
            return;
        }
        if (selectedStatus.value === "Payment Pending" && !selectedPaymentOption) {
            showWarning("Please select a payment option");
            return;
        }

        const description = reason;
        const status = selectedStatus.value;
        const paymentOption = selectedStatus.value === "Payment Pending" ? selectedPaymentOption?.value : null;
        
        let formattedDate = null;
        if (selectedDate) {
            formattedDate = selectedDate.format('YYYY-MM-DD');
        }

        try {
            const result = await createEmployeeResign(
                recordId,
                description,
                paymentOption,
                status,
                formattedDate,
                setLoading
            );
            console.log(result);
            handleReset();
            Swal.fire({
                title: "Success",
                text: "Resignation applied successfully",
                icon: "success",
            });
            
            // Call success callback to refresh parent data
            if (onResignSuccess) {
                onResignSuccess();
            }
            
            onClose();
        } catch (error) {
            setLoading(false);
            console.error('Error during submission:', error);
            showWarning("Failed to submit resignation");
        }
    };

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            width="50vw"
            footer={null}
            maskClosable={false}
            closable={false}
            zIndex={1200}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                }}
            >
                <div className="page-title">
                    <h4>Apply for Resignation</h4>
                    <p className="mb-0">Fill in the required fields to apply</p>
                </div>
                <X
                    style={{
                        fontSize: "20px",
                        color: "#ff9500",
                        cursor: "pointer",
                    }}
                    onClick={onClose}
                />
            </div>
            
            <div className="modal-body">
                <form>
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="employeeName">Name</label>
                                <input
                                    type="text"
                                    id="employeeName"
                                    className="form-control"
                                    value={empRecord?.full_name || `${empRecord?.first_name || ''} ${empRecord?.last_name || ''}`}
                                    disabled
                                />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="resignDate">Date</label>
                                <DatePicker
                                    value={selectedDate}
                                    onChange={(date) => setSelectedDate(date)}
                                    className="form-control"
                                    placeholder="Select date"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="status">Status</label>
                                <Select
                                    value={selectedStatus}
                                    options={statusOptions}
                                    className="select"
                                    placeholder="Select the status"
                                    onChange={setSelectedStatus}
                                />
                            </div>
                        </div>
                        {selectedStatus?.value === "Payment Pending" && (
                            <div className="col-12">
                                <div className="input-blocks">
                                    <label htmlFor="paymentOption">Payment Options</label>
                                    <Select
                                        value={selectedPaymentOption}
                                        options={paymentOptions}
                                        className="select"
                                        placeholder="Select the payment option"
                                        onChange={setSelectedPaymentOption}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="col-12">
                            <div className="input-blocks">
                                <label htmlFor="reason">Reason</label>
                                <textarea
                                    value={reason || ''}
                                    id="reason"
                                    className="form-control"
                                    rows="4"
                                    placeholder="Enter the reason"
                                    onChange={(e) => setReason(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </form>
                
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "20px",
                    }}
                >
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => {
                            onClose();
                            handleReset();
                        }}
                    >
                        Close
                    </button>
                    <div>
                        <button
                            type="button"
                            className="btn btn-reset me-2"
                            onClick={handleReset}
                        >
                            Reset
                        </button>
                        <button 
                            className="btn btn-submit" 
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? <Spin size="small" /> : "Apply"}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

EmployeeResignModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    empRecord: PropTypes.object.isRequired,
    recordId: PropTypes.string.isRequired,
    onResignSuccess: PropTypes.func,
};

export default EmployeeResignModal;
